import { AccountDetails } from '@tradingblock/types';

/** Must match `IpGeoEntryJson` from apiserver `ipGeolocationEndpoints`. */
export interface IpGeoDetails {
  label: string;
  countryCode2: string;
  countryName: string;
  org: string;
  timeZone: string;
  /** From API; used for maps only, not shown in the Security table or tooltips. */
  latitude: string;
  longitude: string;
}

const EMPTY_GEO: IpGeoDetails = {
  label: '',
  countryCode2: '',
  countryName: '',
  org: '',
  timeZone: '',
  latitude: '',
  longitude: '',
};

function parseCoordToString(c: unknown): string {
  if (c == null) {
    return '';
  }
  if (typeof c === 'number' && !isNaN(c)) {
    return String(c);
  }
  if (typeof c === 'string') {
    return c.trim();
  }
  return '';
}

export function toIpGeoDetails(v: unknown): IpGeoDetails {
  if (v == null) {
    return { ...EMPTY_GEO };
  }
  if (typeof v === 'string') {
    return { ...EMPTY_GEO, label: v.trim() };
  }
  if (typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    const label = typeof o.label === 'string' ? o.label.trim() : '';
    if (!label) {
      return { ...EMPTY_GEO };
    }
    return {
      label,
      countryCode2: typeof o.countryCode2 === 'string' ? o.countryCode2.trim() : '',
      countryName: typeof o.countryName === 'string' ? o.countryName.trim() : '',
      org: typeof o.org === 'string' ? o.org.trim() : '',
      timeZone: typeof o.timeZone === 'string' ? o.timeZone.trim() : '',
      latitude: parseCoordToString(o.latitude),
      longitude: parseCoordToString(o.longitude),
    };
  }
  return { ...EMPTY_GEO };
}

function looksLikeIpv4Key(k: string): boolean {
  return /^(25[0-5]|2[0-4]\d|1?\d{1,2})(\.(25[0-5]|2[0-4]\d|1?\d{1,2})){3}$/.test(k.trim());
}

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1?\d{1,2})(\.(25[0-5]|2[0-4]\d|1?\d{1,2})){3}$/;

/**
 * Loopback / RFC1918 / CGNAT / link-local — geolocation vendors reject these (HTTP 423 bogon).
 * Align with `shouldSkipVendorIpv4Geo` in apiserver `ipGeolocationEndpoints.ts`.
 */
export function shouldSkipExternalIpGeolocationLookup(ip: string): boolean {
  const t = ip.trim();
  if (!t) {
    return true;
  }
  if (t.includes(':')) {
    const lower = t.toLowerCase();
    if (lower === '::1') {
      return true;
    }
    if (lower.startsWith('fe80:')) {
      return true;
    }
    const first = lower.split(':')[0] || '';
    if (first.length >= 2 && (first.startsWith('fc') || first.startsWith('fd'))) {
      return true;
    }
    return false;
  }
  if (!IPV4_RE.test(t)) {
    return false;
  }
  const parts = t.split('.').map(s => Number(s));
  if (parts.length !== 4 || parts.some(n => n > 255 || n < 0 || !Number.isFinite(n))) {
    return false;
  }
  const [a, b] = parts;
  if (a === 10) {
    return true;
  }
  if (a === 172 && b >= 16 && b <= 31) {
    return true;
  }
  if (a === 192 && b === 168) {
    return true;
  }
  if (a === 127) {
    return true;
  }
  if (a === 169 && b === 254) {
    return true;
  }
  if (a === 100 && b >= 64 && b <= 127) {
    return true;
  }
  if (a === 0) {
    return true;
  }
  return false;
}

/**
 * Normalize IPs before comparing “session” vs row (strip IPv4-mapped IPv6 prefix; lowercase IPv6).
 */
export function normalizeIpForSessionCompare(raw: string): string {
  let s = raw.trim();
  if (s.indexOf('::ffff:') === 0) {
    s = s.slice(7);
  }
  if (s.includes(':')) {
    return s.toLowerCase();
  }
  return s;
}

/**
 * Normalizes `POST /ip-geo/lookup` JSON to a map of IP → details (strings are legacy label-only).
 */
export function parseIpGeoResponse(
  raw: unknown,
  requestedIps: string[],
  depth: number = 0
): Record<string, IpGeoDetails> {
  if (depth > 3) {
    return {};
  }
  if (Array.isArray(raw)) {
    const m: Record<string, IpGeoDetails> = {};
    for (let i = 0; i < Math.min(raw.length, requestedIps.length); i++) {
      const v = toIpGeoDetails(raw[i]);
      const ip = requestedIps[i];
      if (ip && v.label) {
        m[ip] = v;
      }
    }
    return m;
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const o = raw as Record<string, unknown>;
  const inner =
    o.data && typeof o.data === 'object'
      ? o.data
      : o.Payload && typeof o.Payload === 'object' && !Array.isArray(o.Payload)
        ? o.Payload
        : o.payload && typeof o.payload === 'object' && !Array.isArray(o.payload)
          ? o.payload
          : o.result && typeof o.result === 'object' && !Array.isArray(o.result)
            ? o.result
            : null;
  if (inner) {
    const nested = parseIpGeoResponse(inner, requestedIps, depth + 1);
    if (Object.keys(nested).length > 0) {
      return nested;
    }
  }
  const keys = Object.keys(o);
  const withIpv4Keys = keys.filter(k => looksLikeIpv4Key(k));
  if (withIpv4Keys.length > 0) {
    const m: Record<string, IpGeoDetails> = {};
    for (const k of withIpv4Keys) {
      const d = toIpGeoDetails(o[k]);
      /* Include empty labels so server-sent placeholders for bogons merge and lookups stop retrying. */
      m[k.trim()] = d;
    }
    return m;
  }
  if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
    const m: Record<string, IpGeoDetails> = {};
    const sorted = [...keys].sort((a, b) => Number(a) - Number(b));
    for (let i = 0; i < sorted.length; i++) {
      const d = toIpGeoDetails(o[sorted[i]]);
      const ip = requestedIps[i];
      if (ip && d.label) {
        m[ip] = d;
      }
    }
    return m;
  }
  return {};
}

export function lookupIpGeo(
  map: Record<string, IpGeoDetails>,
  ipRaw: string | undefined | null
): IpGeoDetails | undefined {
  if (ipRaw == null) {
    return undefined;
  }
  const t = String(ipRaw).trim();
  if (t === '') {
    return undefined;
  }
  if (map[t] && map[t].label) {
    return map[t];
  }
  for (const k of Object.keys(map)) {
    if (k.trim() === t) {
      return map[k];
    }
  }
  return undefined;
}

/** Regional-indicator flag from ISO 3166-1 alpha-2 (e.g. US → 🇺🇸). */
export function countryCodeToFlagEmoji(code2: string | undefined): string {
  if (!code2 || code2.length < 2) {
    return '';
  }
  const c = code2.toUpperCase();
  if (c.length !== 2 || !/^[A-Z]{2}$/.test(c)) {
    return '';
  }
  const A = 0x1f1e6;
  return String.fromCodePoint(c.charCodeAt(0) - 65 + A) + String.fromCodePoint(c.charCodeAt(1) - 65 + A);
}

/** First segment of "City, Region, Country" for compact display. */
export function firstSegmentFromLabel(label: string | undefined): string {
  if (!label) {
    return '';
  }
  const part = label.split(',')[0];
  return part ? part.trim() : '';
}

export function buildMapsSearchUrl(query: string): string {
  const q = query.trim();
  if (!q) {
    return '';
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

/**
 * Prefer vendor lat/long for a pin-accurate link; fall back to city/region/country text search.
 * Coordinates are not rendered in the Security UI; only used in this href.
 */
export function buildMapsUrlForGeo(geo: IpGeoDetails | undefined): string {
  if (!geo) {
    return '';
  }
  const latStr = (geo.latitude || '').trim();
  const lonStr = (geo.longitude || '').trim();
  if (latStr !== '' && lonStr !== '') {
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    if (
      !isNaN(lat) &&
      !isNaN(lon) &&
      lat >= -90 &&
      lat <= 90 &&
      lon >= -180 &&
      lon <= 180
    ) {
      return `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lon}`)}`;
    }
  }
  return buildMapsSearchUrl(geo.label);
}

function accountProfileCountryString(details: AccountDetails | undefined | null): string | undefined {
  if (!details) {
    return undefined;
  }
  const p = details.physicalAddress && details.physicalAddress.country;
  if (p && String(p).trim() !== '') {
    return String(p).trim();
  }
  const m = details.mailingAddress && details.mailingAddress.country;
  if (m && String(m).trim() !== '') {
    return String(m).trim();
  }
  return undefined;
}

function profileMatchesGeoCode(profile: string, code2: string, countryName: string): boolean {
  const pl = profile.toLowerCase();
  const code = code2.toUpperCase();
  if (code === 'US' && (pl.includes('united states') || pl === 'us' || pl === 'usa' || pl.includes('u.s'))) {
    return true;
  }
  if (code === 'SE' && pl.includes('sweden')) {
    return true;
  }
  if (code === 'GB' && (pl.includes('united kingdom') || pl.includes('england') || pl.includes('uk'))) {
    return true;
  }
  if (countryName) {
    const cn = countryName.toLowerCase();
    if (pl.includes(cn) || cn.includes(pl.slice(0, Math.min(pl.length, 5)))) {
      return true;
    }
  }
  if (pl.length > 2 && (pl.includes('canada') && code === 'CA')) {
    return true;
  }
  if (pl.length > 2 && (pl.includes('australia') && code === 'AU')) {
    return true;
  }
  return false;
}

export function isUnusualVersusProfile(
  profileCountry: string | undefined,
  geo: IpGeoDetails | undefined
): boolean {
  if (!profileCountry || !geo) {
    return false;
  }
  if (!geo.label) {
    return false;
  }
  if (geo.countryCode2 || geo.countryName) {
    return !profileMatchesGeoCode(profileCountry, geo.countryCode2, geo.countryName);
  }
  return false;
}

function modeCountryCode(
  codeCounts: Map<string, number>
): { code: string; count: number } | null {
  let best: string | null = null;
  let n = 0;
  for (const [c, v] of codeCounts) {
    if (c && v > n) {
      best = c;
      n = v;
    }
  }
  return best && n >= 2 ? { code: best, count: n } : null;
}

/** Dominant country among a list of codes (min 2 rows sharing that code). */
export function isUnusualVersusMode(
  thisCode2: string | undefined,
  allCodes: (string | undefined)[]
): boolean {
  if (!thisCode2) {
    return false;
  }
  const m = new Map<string, number>();
  for (const c of allCodes) {
    if (c) {
      m.set(c, (m.get(c) || 0) + 1);
    }
  }
  const mode = modeCountryCode(m);
  if (!mode) {
    return false;
  }
  return thisCode2.toUpperCase() !== mode.code.toUpperCase();
}

/** `olderCodes` = country codes of the next N *older* logins (row index+1.. in newest-first list). */
export function isUnusualVersusLastN(
  thisCode2: string | undefined,
  olderCodes: (string | undefined)[],
  n: number
): boolean {
  if (!thisCode2) {
    return false;
  }
  const window = olderCodes.slice(0, n).filter(function (c) {
    return Boolean(c);
  }) as string[];
  if (window.length < 2) {
    return false;
  }
  const m = new Map<string, number>();
  for (const c of window) {
    m.set(c, (m.get(c) || 0) + 1);
  }
  const mode = modeCountryCode(m);
  if (!mode) {
    return false;
  }
  return thisCode2.toUpperCase() !== mode.code.toUpperCase();
}

export interface RowSecurityMeta {
  unusual: boolean;
  sameSession: boolean;
  showWasThisYou: boolean;
}

const LAST_N_LOGINS = 6;

export function buildRowSecurityMeta(
  rowIp: string,
  profileCountry: string | undefined,
  geo: IpGeoDetails | undefined,
  /** Country codes in newest-first order (for global mode + last-N). */
  allGeoCodesNewestFirst: (string | undefined)[],
  /** For this row’s index in newest-first: codes of the next N *older* logins (slices from i+1). */
  olderLoginCountryCodes: (string | undefined)[],
  sessionClientIp: string | undefined
): RowSecurityMeta {
  const uProf = isUnusualVersusProfile(profileCountry, geo);
  const c2 = geo && geo.countryCode2;
  const nOlderWithCode = olderLoginCountryCodes.filter(c => c).length;
  const uLastN =
    nOlderWithCode >= 2
      ? isUnusualVersusLastN(c2, olderLoginCountryCodes, LAST_N_LOGINS)
      : isUnusualVersusMode(c2, allGeoCodesNewestFirst);
  const unusual = uProf || uLastN;
  /*
   * Session badge = “this row’s IP matches GET /client-ip” when both are public/comparable IPs.
   * (AuthHistoryTable passes sessionIp only when client-ip is comparable; private client-ip ⇒ no badge.)
   */
  const sessionTrim = sessionClientIp ? sessionClientIp.trim() : '';
  const rowTrim = rowIp ? rowIp.trim() : '';
  const sessionComparable =
    !!sessionTrim && !shouldSkipExternalIpGeolocationLookup(sessionTrim);
  const rowComparable =
    !!rowTrim && !shouldSkipExternalIpGeolocationLookup(rowTrim);
  const sameSession =
    sessionComparable &&
    rowComparable &&
    normalizeIpForSessionCompare(sessionTrim) === normalizeIpForSessionCompare(rowTrim);
  return {
    unusual,
    sameSession,
    showWasThisYou: unusual,
  };
}

function escapeCsv(f: string): string {
  if (f.indexOf(',') >= 0 || f.indexOf('"') >= 0 || f.indexOf('\n') >= 0) {
    return `"${f.replace(/"/g, '""')}"`;
  }
  return f;
}

export function buildLoginHistoryCsv(
  rows: { TimeStamp: string; ip: string; geo?: IpGeoDetails }[]
): string {
  const header = 'Date,Time,IP,Location,Country code,Network,Time zone\n';
  const body = rows
    .map(r => {
      const t = r.TimeStamp;
      const d = t ? new Date(t.indexOf('Z') >= 0 || t.indexOf('+') >= 0 ? t : t + 'Z') : new Date(0);
      const date = isNaN(d.getTime()) ? '' : d.toLocaleDateString();
      const time = isNaN(d.getTime()) ? '' : d.toLocaleTimeString();
      const g = r.geo;
      return [
        date,
        time,
        r.ip,
        g ? g.label : '',
        g ? g.countryCode2 : '',
        g ? g.org : '',
        g ? g.timeZone : '',
      ]
        .map(escapeCsv)
        .join(',');
    })
    .join('\n');
  return header + body;
}

export function downloadTextFile(filename: string, text: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function getProfileCountryForGeo(details: AccountDetails | undefined | null): string | undefined {
  return accountProfileCountryString(details);
}
